import { BasicResponseDTO } from './basicResponse.dto';
import { parseBool } from './util/util';

/**
 * When updating keys, you MUST also update SETTINGS_LABELS
 * so the change appears correctly in audit logs.
 */
class GlobKeyTypes {
  readonly SNMP_TRAP_HOST = '';
  readonly SNMP_COMMUNITY = '';
  readonly TOKEN_EXPIRATION = 0; // minutes, 0 = infinite token validity
  readonly TOP_ITEMS = 0;
  readonly VMWARE_SDK = 0;
  readonly PUBLIC_URL = '';
  readonly DATA_RETENTION = 0; // 365;  // days
  readonly REMOVE_FROM_MENU = 0; // = 30; // days
  readonly LKEY: string = '';
  readonly CONF_CHANGES_RETENTION = 0; // 30; // days
  readonly DEFAULT_TIMEZONE = ''; // timezone
  readonly FAILED_LOGIN_ATTEMPTS_LIMIT = 0; // 10;
  readonly SHOW_DELETED_ITEMS = true;
  readonly EVENT_INTERVAL = 0; //60; // minutes
  readonly WELCOME_MESSAGE = '';
  readonly WELCOME_MESSAGE_POSITION: WelcomeMessagePosition = WelcomeMessagePosition.LEFT;
  readonly WELCOME_MESSAGE_ENABLED = false;
  readonly DECIMAL_SEPARATOR = '.'; // '.' or ','
  readonly CSV_SEPARATOR = ','; // ',' or ';'
  readonly LOG_INTERVAL = 60 * 60 * 24; // last 24 hours by default, in seconds
  readonly INSTANCE_LABEL = '';
  readonly DASHBOARD_AUTOPLAY_INTERVAL = 0; // 30; // seconds between dashboards
}

class AlertingKeyTypes {
  readonly NAGIOS = false;
  readonly IS_SCRIPT = false;
  readonly SCRIPT = '';
  readonly INCLUDE_GRAPHS = 0;
  readonly REPEAT_TIME = 0;
  readonly PEAK_TIME = 0;
  readonly XORMON_URL = '';
  readonly ALARMONE_URL = '';
  readonly SEND_PER_ITEM = false;
  readonly HISTORY_RETENTION = 0; //14; // days
  readonly SAN_PORT_ALERT_MODE: SanPortAlertMode = SanPortAlertMode.DEFAULT;
}

class SmtpKeyTypes {
  readonly HOST = '';
  readonly USER = '';
  readonly PASS = '';
  readonly PORT = '';
  readonly ENCRYPTION = '';
  readonly MAIL_FROM = '';
}

class ServicenowKeyTypes {
  readonly INSTANCE = '';
  readonly USER = '';
  readonly PASS = '';
  readonly TYPE = '';
  readonly CLASS = '';
  readonly CUSTOM = '';
  readonly MESSAGE = '';
  readonly SEND_OK = true;
  readonly PROXY = false;
  readonly PROXY_HOST = '';
  readonly PROXY_PORT = '';
  readonly PROXY_USER = '';
  readonly PROXY_PASS = '';
  readonly PROXY_SSL = false;
}

class CertKeyTypes {
  readonly KEY = '';
  readonly CRT = '';
}

class LdapKeyTypes {
  readonly TIMEOUT_SEC = 0;
  readonly PAGE_SIZE = 0;
}

class GrafanaKeyTypes {
  readonly PROTOCOL = '';
  readonly HOST = '';
  readonly PORT = '';
  readonly TOKEN = '';
  readonly SYNC = '';
  readonly FOLDER = '';
  readonly FOLDER_ID = '';
  readonly USER_ID = '';
}

class ProxyKeyTypes {
  readonly ALERTING_MAILS = '';
}

class PasswordKeyTypes {
  readonly ENABLED = false;
  readonly MIN_LENGTH = 5; // minimum in UsersForm
  readonly NUMERALS = true; // require a numeral
  readonly UPPERCASE = true; // require a uppercase letter
  readonly LOWERCASE = true; // require a lowercase letter
  readonly SPECIAL = true; // require a special character
  readonly EXPIRATION_DAYS = 0;
  readonly HISTORY_COUNT = 1; // block reuse of last N passwords (future extension)
}

class SsoKeyTypes {
  readonly ENABLED = false;
  readonly SERVER = '';
  readonly CLIENT_ID = '';
  readonly CLIENT_SECRET = '';
  readonly ROLES_PROP = '';
  readonly REDIRECT_HOST = '';
  readonly READONLY_ROLE = '';
  readonly SCOPES = '';
}

class LLMKeyTypes {
  readonly PROVIDER = ''; // e. g. ollama, deepseek, openai, anthropic, watsonx
  readonly ENDPOINT = ''; // ful endpoint URL, e. g. http://10.22.66.39:11434/api/chat
  readonly MODEL = ''; // model name, e. g. qwen3:1.7b
  readonly API_KEY = ''; // API key for authentication, if required
  readonly WORKSPACE_ID = ''; // Anthropic only, e. g. wrkspc_011CZkZaBF1tNoB5wlCeusgy; empty for a normal workspace key
  readonly ENABLED = false;
  readonly PROJECT_ID = '';
  readonly MCP_ENABLED = false;
}

// own prefix, not part of LLMKeyTypes: pyxbot doesn't need a server-side LLM, so an LLM key must not gate it
class PyxbotKeyTypes {
  readonly MCP_ENABLED = false;
  readonly TRIAL_START = ''; // encrypted time of the first Xbot use on a free installation
}

class HealthMonitorKeyTypes {
  readonly ENABLED = true;
  readonly ADMIN_GROUP = true; // send emails to users in admin group
  readonly EMAIL_GROUPS = true; // send emails to health monitor mail groups
  readonly FS_THRESHOLD = 85; // percentage of occupied filesystem space
  readonly CPU_THRESHOLD = 80;
  readonly MEM_THRESHOLD = 80;
  readonly PAGING_THRESHOLD = 80;
}

// netflow-collector settings; the values are the defaults
class NetflowKeyTypes {
  readonly ENABLED = true; // false = ports closed, nothing collected or stored
  readonly PORT = 2055; // UDP, NetFlow v5/v9 + IPFIX
  readonly SFLOW_PORT = 6343; // UDP
  readonly DEDUP = true; // count a flow reported by several exporters once
  readonly MIN_CONVERSATION_BYTES = 102400; // 100 kB per flush interval
  readonly MAX_CONVERSATIONS = 250; // largest conversations stored per flush interval
}

export const NETFLOW_DEFAULTS = new NetflowKeyTypes();

// plain string enum: key is what's stored, value is just the UI label
export enum LLM_PROVIDER {
  ollama = 'Ollama (self-hosted)',
  openai = 'OpenAI',
  anthropic = 'Anthropic (Claude)',
  deepseek = 'DeepSeek',
  watsonx = 'WatsonX',
}

// goes into a request header, so only this shape is stored and sent
export const LLM_WORKSPACE_ID_PATTERN = /^wrkspc_[A-Za-z0-9_-]{1,64}$/;

// empty means "no workspace header", which a normal workspace key needs
export function isLlmWorkspaceIdAllowed(value: string | null | undefined): boolean {
  return value == null || value === '' || LLM_WORKSPACE_ID_PATTERN.test(value);
}

// older settings pages stored the label instead of the key, so accept both
export function llmProviderKey(value: string | null | undefined): keyof typeof LLM_PROVIDER | undefined {
  if (value == null) {
    return undefined;
  }
  for (const [key, label] of Object.entries(LLM_PROVIDER)) {
    if (value === key || value === label) {
      return key as keyof typeof LLM_PROVIDER;
    }
  }
  return undefined;
}

export enum WelcomeMessagePosition {
  LEFT = 'left',
  RIGHT = 'right',
  TOP = 'top',
  BOTTOM = 'bottom'
}

export enum SanPortAlertMode {
  DEFAULT = 'default',
  OFFLINE = 'offline',
  ANY_CHANGE = 'any_change',
}

const ALERTING_ = 'ALERTING_';
const GLOB_ = 'GLOB_';
const SMTP_ = 'SMTP_';
const CERT_ = 'CERT_';
const LDAP_ = 'LDAP_';
const SERVICENOW_ = 'SERVICENOW_';
const GRAFANA_ = 'GRAFANA_';
const PROXY_ = 'PROXY_';
const PASSWORD_ = 'PASSWORD_';
const SSO_ = 'SSO_';
const LLM_ = 'LLM_';
const PYXBOT_ = 'PYXBOT_';
const HEALTHMONITOR_ = 'HEALTHMONITOR_';
const NETFLOW_ = 'NETFLOW_';

export class SettingKey<T = Record<string, any>> {
  static readonly ALERTING = new SettingKey(ALERTING_, new AlertingKeyTypes());
  static readonly GLOB = new SettingKey(GLOB_, new GlobKeyTypes());
  static readonly SMTP = new SettingKey(SMTP_, new SmtpKeyTypes());
  static readonly LDAP = new SettingKey(LDAP_, new LdapKeyTypes());
  static readonly SERVICENOW = new SettingKey(SERVICENOW_, new ServicenowKeyTypes());
  static readonly GRAFANA = new SettingKey(GRAFANA_, new GrafanaKeyTypes());
  static readonly PROXY = new SettingKey(PROXY_, new ProxyKeyTypes());
  static readonly PASSWORD = new SettingKey(PASSWORD_, new PasswordKeyTypes());
  static readonly SSO = new SettingKey(SSO_, new SsoKeyTypes());
  static readonly LLM = new SettingKey(LLM_, new LLMKeyTypes());
  static readonly PYXBOT = new SettingKey(PYXBOT_, new PyxbotKeyTypes());
  static readonly HEALTHMONITOR = new SettingKey(HEALTHMONITOR_, new HealthMonitorKeyTypes());
  static readonly NETFLOW = new SettingKey(NETFLOW_, NETFLOW_DEFAULTS);

  readonly PREFIX: string;
  protected readonly keyTypes: T;

  private constructor(prefix: string, keyTypes: T) {
    this.PREFIX = prefix;
    this.keyTypes = keyTypes;
  }

  private getType(propertyName: string) {
    return typeof this.keyTypes[propertyName.substring(propertyName.startsWith(this.PREFIX) ? this.PREFIX.length : 0)];
  }

  /**
   * Helper to get name of property
   * @param propertyName property key
   * @returns name of property
   */
  get(propertyName: keyof T) {
    return (this.PREFIX + (propertyName as string)) as keyof SettingsItem;
  }

  // getValue(propertyName: keyof T, settings: SettingsItem){
  //   const key = this.get(propertyName);
  //   const value:string = settings[key];
  //   if(value==null) return value;
  //   switch(this.getType(key)){
  //     case 'boolean':
  //       return parseBool(value);
  //     case 'number':
  //       return parseInt(value);
  //     default:
  //       return value;
  //   }
  // }

  /**
   * Changes value types according to keyTypes
   * @param settings object with values to be re-typed
   */
  reType(settings: SettingsItem) {
    Object.entries(settings).forEach(([key, value]) => {
      if (value != null) {
        switch (this.getType(key)) {
          case 'boolean':
            settings[key] = parseBool(value);
            break;
          case 'number':
            settings[key] = parseInt(value);
            break;
        }
      }
    });
  }
}

// type TypedSetting<P extends string, T> = { [K in (string & keyof T) as `${P}${K}`]: T[K] };
// type SettingsItemTyped = Partial<TypedSetting<typeof ALERTING_, AlertingKeyTypes> | TypedSetting<typeof GLOB_, GlobKeyTypes>>;
//let test: SettingsItemTyped = { ALERTING_INCLUDE_GRAPHS: 2, GLOB_DATA_RETENTION: 30, };
//const val = test.ALERTING_INCLUDE_GRAPHS;

export type SettingsItem = Partial<
  Record<
    | `${typeof ALERTING_}${keyof AlertingKeyTypes}`
    | `${typeof GLOB_}${keyof GlobKeyTypes}`
    | `${typeof SMTP_}${keyof SmtpKeyTypes}`
    | `${typeof CERT_}${keyof CertKeyTypes}`
    | `${typeof LDAP_}${keyof LdapKeyTypes}`
    | `${typeof SERVICENOW_}${keyof ServicenowKeyTypes}`
    | `${typeof GRAFANA_}${keyof GrafanaKeyTypes}`
    | `${typeof PROXY_}${keyof ProxyKeyTypes}`
    | `${typeof PASSWORD_}${keyof PasswordKeyTypes}`
    | `${typeof SSO_}${keyof SsoKeyTypes}`
    | `${typeof HEALTHMONITOR_}${keyof HealthMonitorKeyTypes}`
    | `${typeof LLM_}${keyof LLMKeyTypes}`
    | `${typeof PYXBOT_}${keyof PyxbotKeyTypes}`
    | `${typeof NETFLOW_}${keyof NetflowKeyTypes}`,
    string
  >
>;

export class SettingsResponseDTO extends BasicResponseDTO {
  data: SettingsItem;
}

export class MailTestDTO {
  mail: string;
}

export interface WelcomeMessageSettings {
  GLOB_WELCOME_MESSAGE: string | null;
  GLOB_WELCOME_MESSAGE_POSITION: string;
  GLOB_WELCOME_MESSAGE_ENABLED: boolean;
}

export const SETTINGS_LABELS: Record<string, string> = {
  // GLOB
  GLOB_DATA_RETENTION: "Delete from database after",
  GLOB_DEFAULT_TIMEZONE: "Default timezone",
  GLOB_EVENT_INTERVAL: "HW Event interval",
  GLOB_FAILED_LOGIN_ATTEMPTS_LIMIT: "Failed logins attempts limit",
  GLOB_REMOVE_FROM_MENU: "Remove from UI after",
  GLOB_SHOW_DELETED_ITEMS: "Show deleted items in graphs",
  GLOB_SNMP_COMMUNITY: "SNMP community string",
  GLOB_SNMP_TRAP_HOST: "SNMP trap host",
  GLOB_TOKEN_EXPIRATION: "Session timeout",
  GLOB_TOP_ITEMS: "Default graph items limit",
  GLOB_WELCOME_MESSAGE: "Custom login banner",
  GLOB_WELCOME_MESSAGE_POSITION: "Custom login banner position",
  GLOB_WELCOME_MESSAGE_ENABLED: "Custom login banner enabled",
  GLOB_DECIMAL_SEPARATOR: "Decimal separator",
  GLOB_CSV_SEPARATOR: "CSV separator",
  GLOB_INSTANCE_LABEL: "Browser tab instance label",
  GLOB_DASHBOARD_AUTOPLAY_INTERVAL: "Dashboard autoplay interval",

  // ALERTING
  ALERTING_NAGIOS: "Nagios integration enabled",
  ALERTING_IS_SCRIPT: "Custom alerting script enabled",
  ALERTING_SCRIPT: "Custom alerting script content",
  ALERTING_INCLUDE_GRAPHS: "Alert emails include graphs",
  ALERTING_REPEAT_TIME: "Alert repeat interval",
  ALERTING_PEAK_TIME: "Peak alerting time window",
  ALERTING_XORMON_URL: "Xormon integration URL",
  ALERTING_ALARMONE_URL: "AlarmOne integration URL",
  ALERTING_SEND_PER_ITEM: "Send individual alerts per item",
  ALERTING_HISTORY_RETENTION: "Alert history retention period",
  ALERTING_SAN_PORT_ALERT_MODE: "SAN port status HW alerting mode",

  // SMTP
  SMTP_HOST: "SMTP server address",
  SMTP_USER: "SMTP username",
  SMTP_PASS: "SMTP password",
  SMTP_PORT: "SMTP port",
  SMTP_ENCRYPTION: "SMTP encryption",
  SMTP_MAIL_FROM: "SMTP mail from",

  // SERVICENOW
  SERVICENOW_INSTANCE: "ServiceNow instance name",
  SERVICENOW_USER: "ServiceNow user account",
  SERVICENOW_PASS: "ServiceNow password",
  SERVICENOW_TYPE: "ServiceNow ticket type",
  SERVICENOW_CLASS: "ServiceNow ticket class",
  SERVICENOW_CUSTOM: "ServiceNow custom field configuration",
  SERVICENOW_SEND_OK: "Send OK status to ServiceNow",

  // LDAP
  LDAP_TIMEOUT_SEC: "LDAP connection timeout",
  LDAP_PAGE_SIZE: "LDAP page size for results",

  // GRAFANA
  GRAFANA_PROTOCOL: "Grafana connection protocol",
  GRAFANA_HOST: "Grafana host",
  GRAFANA_PORT: "Grafana port",
  GRAFANA_TOKEN: "Grafana access token",
  GRAFANA_SYNC: "Grafana data synchronization enabled",
  GRAFANA_FOLDER: "Grafana folder name",
  GRAFANA_FOLDER_ID: "Grafana folder ID",
  GRAFANA_USER_ID: "Grafana user ID",

  // CERT
  CERT_KEY: "Added SSL certificate",

  // PROXY
  PROXY_ALERTING_MAILS: "Proxy alerting email configuration",

  // PASSWORD POLICY
  PASSWORD_ENABLED: "Password policy",
  PASSWORD_MIN_LENGTH: "Password minimum length",
  PASSWORD_NUMERALS: "Password must contain digits",
  PASSWORD_UPPERCASE: "Password must contain uppercase letters",
  PASSWORD_LOWERCASE: "Password must contain lowercase letters",
  PASSWORD_SPECIAL: "Password must contain special characters",
  PASSWORD_HISTORY_COUNT: "Password history count",

  // SSO
  SSO_ENABLED: "Single Sign-On (SSO) enabled",
  SSO_SERVER: "SSO server address",
  SSO_CLIENT_ID: "SSO client ID",
  SSO_CLIENT_SECRET: "SSO client secret",
  SSO_ROLES_PROP: "SSO roles property mapping",

  // LLM
  LLM_PROVIDER: "LLM provider",
  LLM_ENDPOINT: "LLM endpoint URL",
  LLM_MODEL: "LLM model name",
  LLM_API_KEY: "LLM API key",
  LLM_WORKSPACE_ID: "LLM workspace ID (Anthropic)",
  LLM_ENABLED: "LLM integration enabled",
  LLM_PROJECT_ID: "LLM project ID",

  // PYXBOT
  PYXBOT_MCP_ENABLED: "XBot MCP server enabled",
  PYXBOT_TRIAL_START: "XBot free trial start",

  // Health Monitor
  HEALTHMONITOR_ENABLED: "Enable Health Monitor",
  HEALTHMONITOR_ADMIN_GROUP: "Send Health Monitor emails to admins",
  HEALTHMONITOR_EMAIL_GROUPS: "Send Health Monitor emails to selected email groups",
  HEALTHMONITOR_FS_THRESHOLD: "Health Monitor FS alert percentual threshold",

  // NetFlow
  NETFLOW_ENABLED: "NetFlow collection enabled",
  NETFLOW_PORT: "NetFlow / IPFIX port",
  NETFLOW_SFLOW_PORT: "sFlow port",
  NETFLOW_DEDUP: "NetFlow deduplicate flows from multiple exporters",
  NETFLOW_MIN_CONVERSATION_BYTES: "NetFlow minimum conversation size",
  NETFLOW_MAX_CONVERSATIONS: "NetFlow maximum conversations",
};
